import * as React from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { tJa } from 'core/i18n'
import * as constants from 'static/constants'
import { Button } from 'components/atoms'
import Pagination from 'components/atoms/Pagination'
import { IJobBulkPlan, IPost, ITourSkill, ISkill, IAgentRank, IWindow } from 'static/interfaces'
import { jobService } from 'core/services'
import JobBulkPlanModal from './JobBulkPlanModal'
import { weekDaysChecked } from 'utils/job'
import JobSlot from './JobSlot'

declare var window: IWindow

export interface IJobBulkPlanListProps {
  post: IPost
  jobBulkPlans: IJobBulkPlan[]
  startTimes: string[]
  tour_skills: ITourSkill[]
  skills: ISkill[]
  agent_ranks: IAgentRank[]
  pagination: any
}

const JobBulkPlanList: React.FC<IJobBulkPlanListProps> = ({
  post,
  jobBulkPlans,
  startTimes,
  tour_skills,
  skills,
  agent_ranks,
  pagination,
}) => {
  const title = React.useMemo(() => {
    return `${tJa('generic.job_bulk_plan')} - ${post.short_name}`
  }, [post.short_name])

  const getStatus = React.useCallback((plan: IJobBulkPlan) => {
    return moment(plan.publish_start_at).isAfter(moment())
      ? 'upcoming'
      : moment(plan.publish_end_at).isBefore(moment())
      ? 'ended'
      : 'open'
  }, [])

  const handleCreate = React.useCallback(() => {
    window.globalModal.showModal({
      small: true,
      body: (
        <JobBulkPlanModal
          title={title}
          post={post}
          startTimes={startTimes}
          tour_skills={tour_skills}
          skills={skills}
          agent_ranks={agent_ranks}
        />
      ),
    })
  }, [title, post, startTimes, tour_skills, skills, agent_ranks])

  const handleChangePage = React.useCallback((page: number) => {
    const params = new URLSearchParams(window.location.search)
    params.set('page', String(page))
    location.href = `${location.pathname}?${params.toString()}`
  }, [])

  const handleDelete = React.useCallback(async (id: number) => {
    if (!window.confirm('この募集を削除します。よろしいですか？')) {
      return
    }

    try {
      const { flush } = await jobService.deleteJobBulkPlan(id)
      window.flashMessages.addMessage({
        text: flush.message,
        type: flush.type,
      })
      window.location.reload()
    } catch (e) {
      console.error(e)
    }
  }, [])

  return (
    <Wrapper>
      <div className="header">
        <h2 className="Editor_Title">{title}</h2>
        <Button primary={true} handleClick={handleCreate}>
          {tJa('generic.create_new')}
        </Button>
      </div>
      <div className="list">
        {jobBulkPlans.length === 0 ? (
          <p className="empty">{tJa('generic.job_bulk_plan_empty')}</p>
        ) : (
          <ul>
            {jobBulkPlans.map(plan => (
              <li key={plan.id} className={`${getStatus(plan)}`}>
                <div className="planHeader">
                  <div className="planHeaderLeft">
                    <div className={`status ${getStatus(plan)}`}>
                      {constants.JOB_STATUS_LABELS[getStatus(plan)]}
                    </div>
                    {plan.agent_rank && (
                      <div className="agentRankLabel">{plan.agent_rank.name}</div>
                    )}
                  </div>
                  <div>
                    <span>{tJa('job_bulk_plan.created_at_label')}: </span>
                    {moment(plan.created_at).format('YYYY/MM/DD')}
                  </div>
                </div>
                <div>
                  <div className="label">{tJa('job_bulk_plan.publish_period')}: </div>
                  {moment(plan.publish_start_at).format('YYYY/MM/DD, H:mm')} ~{' '}
                  {moment(plan.publish_end_at).format('YYYY/MM/DD, H:mm')}
                </div>
                <div>
                  <div className="label">{tJa('job_bulk_plan.target_dates')}: </div>
                  {moment(plan.target_date_from).format('YYYY/MM/DD')} ~{' '}
                  {moment(plan.target_date_to).format('YYYY/MM/DD')}
                  <div className="days-of-the-week">
                    {weekDaysChecked(plan).map(day => (
                      <span key={day}>{constants.DAYS_OF_THE_WEEK[day]}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="label">{tJa('job_bulk_plan.start_times')}: </div>
                  {plan.job_bulk_plan_start_times.map(time => (
                    <span className="start-time" key={time.id}>
                      {moment(time.start_time).format('H:mm')}
                    </span>
                  ))}
                </div>
                <div>
                  <div className="label">{tJa('job_bulk_plan.tour_skill')}: </div>
                  {
                    tour_skills.find(
                      tourSkill => tourSkill.id === plan.job_bulk_plan_slots[0].tour_skill_id
                    )?.name
                  }{' '}
                  - <div className="label">{tJa('job_bulk_plan.total_recruitment')}: </div>
                  {plan.job_bulk_plan_slots.reduce(
                    (acc, slot) => acc + slot.direct_max_limit + slot.selection_max_limit,
                    0
                  )}
                </div>
                <div className="jobSlots">
                  {plan.job_bulk_plan_slots.map((jobSlot, _index) => (
                    <JobSlot
                      index={_index}
                      key={jobSlot.id || jobSlot.client_uid || _index}
                      slot={jobSlot}
                      setSlots={() => undefined}
                      skills={skills}
                      editting={false}
                    />
                  ))}
                </div>
                <div className="deleteButton">
                  <Button handleClick={() => handleDelete(plan.id)}>この募集を削除する</Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {pagination && (
        <Pagination
          onChangePageHandler={handleChangePage}
          currentPage={Number(pagination.current_page)}
          prevPage={pagination.prev_page}
          nextPage={pagination.next_page}
          totalPages={pagination.total_pages}
          totalCount={pagination.total_count}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding-bottom: 14px;
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    border-bottom: solid 1px ${constants.COLORS.divider2};
    padding: 8px 16px;
    .Editor_Title {
      padding: 0;
      border-bottom: none;
    }
    .Button {
      min-width: 120px;
      font-size: 14px;
      padding: 6px 16px;
    }
  }
  .list {
    .empty {
      color: ${constants.COLORS.subText};
      padding: 120px 20px;
      text-align: center;
    }
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  li {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 14px;
    border-bottom: 1px solid ${constants.COLORS.divider2};
    padding: 12px 16px;
    background-color: ${constants.COLORS.white};
    &.ended {
      background-color: ${constants.COLORS.cardBG};
      .deleteButton {
        .Button {
          border-color: ${constants.COLORS.divider};
          background: transparent;
          color: ${constants.COLORS.subText};
        }
      }
    }
    > div {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 500;
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        flex-wrap: wrap;
      }
    }
  }
  .planHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .planHeaderLeft {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .agentRankLabel {
      line-height: 16px;
      padding: 3px 12px;
      border-radius: 12px;
      color: ${constants.COLORS.info2};
      background-color: ${constants.COLORS.white};
      border: 1px solid ${constants.COLORS.info2};
    }
    .status {
      line-height: 16px;
      padding: 4px 16px;
      border-radius: 12px;
      font-weight: bold;
      &.upcoming {
        color: ${constants.COLORS.primary};
        border: 1px solid ${constants.COLORS.primary};
      }
      &.open {
        color: ${constants.COLORS.white};
        background-color: ${constants.COLORS.primary};
      }
      &.ended {
        padding: 4px 0;
        color: ${constants.COLORS.subText};
      }
    }
    span {
      color: ${constants.COLORS.subText};
    }
  }
  .label {
    color: ${constants.COLORS.subText};
  }
  .days-of-the-week {
    display: flex;
    align-items: center;
    gap: 4px;
    span {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      font-size: 13px;
      border: 1px solid ${constants.COLORS.divider};
      border-radius: 50%;
      text-align: center;
    }
  }
  .start-time {
    line-height: 16px;
    padding: 4px 8px;
    border: 1px solid ${constants.COLORS.divider};
    border-radius: 4px;
  }
  .jobSlots {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 12px;
    padding-right: 200px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      padding-right: 0;
    }
    .slotView {
      font-size: 14px;
      .tourSkillRank {
        .icon {
          width: 20px;
          height: 20px;
        }
      }
      .tourSkillNumber {
        gap: 4px;
        span {
          font-size: 13px;
          background-color: ${constants.COLORS.cardBG};
          padding: 2px 6px 2px 4px;
          border-radius: 4px;
        }
        small {
          color: ${constants.COLORS.black};
          font-size: 12px;
        }
      }
      .skills .skill {
        padding: 4px 8px 4px 4px;
      }
    }
  }
  .deleteButton {
    position: absolute;
    bottom: 12px;
    right: 16px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      position: static;
      margin-left: auto;
    }
    .Button {
      min-width: 120px;
      height: 32px;
      font-size: 14px;
      padding: 4px 16px;
    }
  }
  .Pagination {
    position: sticky;
    bottom: -16px;
    background-color: ${constants.COLORS.white};
    margin: 0;
    border-top: 1px solid ${constants.COLORS.divider2};
    padding: 16px 0;
  }
`

export default JobBulkPlanList
